import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface ReportData {
  // KPI
  kpi: {
    monthlyRevenue: number;
    monthlyRevenueChange: number;
    totalOffers: number;
    offersChange: number;
    conversionRate: number;
    conversionChange: number;
    newClients: number;
    newClientsChange: number;
    averageOrderValue: number;
    averageProcessingTime: number; // w dniach
    targetRealization: number; // procent realizacji targetu
  };
  
  // Dane sprzedażowe
  salesTrend: Array<{
    period: string;
    sales: number;
    offers: number;
    accepted: number;
    target: number;
  }>;
  
  // Podział produktów
  productDistribution: Array<{
    category: string;
    value: number;
    percentage: number;
    count: number;
    color: string;
  }>;
  
  // Top klienci
  topClients: Array<{
    id: string;
    name: string;
    revenue: number;
    orders: number;
    growth: number;
    avgOrderValue: number;
    lastOrder: string;
  }>;
  
  // Wydajność vs cel
  performanceVsTarget: Array<{
    period: string;
    target: number;
    actual: number;
    percentage: number;
  }>;
  
  // Analiza regionalna
  regionalAnalysis: Array<{
    region: string;
    revenue: number;
    orders: number;
    clients: number;
    avgOrderValue: number;
  }>;
  
  // Analiza materiałów
  materialAnalysis: Array<{
    material: string;
    usage: number; // m²
    revenue: number;
    orders: number;
    avgPrice: number;
  }>;
  
  // Trendy czasowe
  timeTrends: {
    bestDay: string;
    bestHour: number;
    avgResponseTime: number; // w godzinach
    avgClosingTime: number; // w dniach
  };
}

export function useReports(dateRange: 'week' | 'month' | 'quarter' | 'year' = 'month') {
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      
      // Oblicz zakres dat
      const endDate = new Date();
      const startDate = new Date();
      
      switch (dateRange) {
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
      }

      // Pobierz dane
      const [
        { data: offers, error: offersError },
        { data: clients, error: clientsError }
      ] = await Promise.all([
        supabase
          .from('offers')
          .select('*')
          .gte('created_at', startDate.toISOString()),
        
        supabase
          .from('clients')
          .select('*')
      ]);
      
      // Opcjonalnie pobierz produkty (jeśli tabela istnieje)
      let products = [];
      try {
        const { data: prods, error: prodsError } = await supabase
          .from('products')
          .select('*');
        if (prods && !prodsError) products = prods;
      } catch (err) {
        console.log('Tabela products niedostępna - kontynuuję bez niej');
      }
      
      // Opcjonalnie pobierz statystyki (jeśli tabela istnieje)
      let statistics = [];
      try {
        const { data: stats } = await supabase
          .from('offer_statistics')
          .select('*')
          .gte('viewed_at', startDate.toISOString());
        if (stats) statistics = stats;
      } catch (err) {
        console.log('Tabela offer_statistics nie istnieje - pomijam statystyki');
      }

      if (!offers || !clients) {
        throw new Error('Nie udało się pobrać danych');
      }

      // Oblicz KPI
      const kpi = calculateKPI(offers, clients, dateRange);
      
      // Przygotuj dane do wykresów
      const salesTrend = prepareSalesTrend(offers, dateRange);
      const productDistribution = prepareProductDistribution(offers);
      const topClients = prepareTopClients(offers, clients);
      const performanceVsTarget = preparePerformanceVsTarget(offers, dateRange);
      const regionalAnalysis = prepareRegionalAnalysis(offers, clients);
      const materialAnalysis = prepareMaterialAnalysis(offers);
      const timeTrends = calculateTimeTrends(offers, statistics);

      setData({
        kpi,
        salesTrend,
        productDistribution,
        topClients,
        performanceVsTarget,
        regionalAnalysis,
        materialAnalysis,
        timeTrends
      });

    } catch (err) {
      console.error('Report error:', err);
      setError(err instanceof Error ? err.message : 'Błąd generowania raportu');
    } finally {
      setLoading(false);
    }
  };

  // Funkcje pomocnicze
  const calculateKPI = (offers: any[], clients: any[], dateRange: string) => {
    const now = new Date();
    const thisMonth = offers.filter(o => {
      const date = new Date(o.created_at);
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    });
    
    const lastMonth = offers.filter(o => {
      const date = new Date(o.created_at);
      const lastMonthDate = new Date(now);
      lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
      return date.getMonth() === lastMonthDate.getMonth() && 
             date.getFullYear() === lastMonthDate.getFullYear();
    });

    const acceptedThisMonth = thisMonth.filter(o => o.status === 'accepted');
    const acceptedLastMonth = lastMonth.filter(o => o.status === 'accepted');
    
    const revenueThisMonth = acceptedThisMonth.reduce((sum, o) => sum + (o.total_net || 0), 0);
    const revenueLastMonth = acceptedLastMonth.reduce((sum, o) => sum + (o.total_net || 0), 0);
    
    const conversionThisMonth = thisMonth.length > 0 ? (acceptedThisMonth.length / thisMonth.length) * 100 : 0;
    const conversionLastMonth = lastMonth.length > 0 ? (acceptedLastMonth.length / lastMonth.length) * 100 : 0;
    
    // Nowi klienci (przybliżone - zakładamy że klient jest nowy jeśli ma tylko 1 ofertę)
    const clientOfferCounts = new Map();
    offers.forEach(o => {
      if (o.client?.nip) {
        clientOfferCounts.set(o.client.nip, (clientOfferCounts.get(o.client.nip) || 0) + 1);
      }
    });
    const newClients = Array.from(clientOfferCounts.values()).filter(count => count === 1).length;
    
    // Średni czas przetwarzania (od utworzenia do zaakceptowania)
    const processingTimes = acceptedThisMonth
      .filter(o => o.updated_at)
      .map(o => {
        const created = new Date(o.created_at);
        const updated = new Date(o.updated_at);
        return (updated.getTime() - created.getTime()) / (1000 * 60 * 60 * 24); // dni
      });
    const avgProcessingTime = processingTimes.length > 0 
      ? processingTimes.reduce((a, b) => a + b, 0) / processingTimes.length 
      : 0;
    
    // Target (przykładowy - 100k miesięcznie)
    const monthlyTarget = 100000;
    const targetRealization = (revenueThisMonth / monthlyTarget) * 100;

    return {
      monthlyRevenue: revenueThisMonth,
      monthlyRevenueChange: revenueLastMonth > 0 
        ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100 
        : 0,
      totalOffers: thisMonth.length,
      offersChange: lastMonth.length > 0 
        ? ((thisMonth.length - lastMonth.length) / lastMonth.length) * 100 
        : 0,
      conversionRate: conversionThisMonth,
      conversionChange: conversionThisMonth - conversionLastMonth,
      newClients,
      newClientsChange: 0, // TODO: Oblicz rzeczywistą zmianę
      averageOrderValue: acceptedThisMonth.length > 0 
        ? revenueThisMonth / acceptedThisMonth.length 
        : 0,
      averageProcessingTime: avgProcessingTime,
      targetRealization
    };
  };

  const prepareSalesTrend = (offers: any[], dateRange: string) => {
    const periods = dateRange === 'week' ? 7 : 
                   dateRange === 'month' ? 6 : 
                   dateRange === 'quarter' ? 3 : 12;
    
    const data = [];
    const now = new Date();
    
    for (let i = periods - 1; i >= 0; i--) {
      const periodStart = new Date(now);
      const periodEnd = new Date(now);
      
      if (dateRange === 'week') {
        periodStart.setDate(now.getDate() - i);
        periodEnd.setDate(now.getDate() - i);
      } else if (dateRange === 'month') {
        periodStart.setDate(now.getDate() - (i + 1) * 5);
        periodEnd.setDate(now.getDate() - i * 5);
      } else if (dateRange === 'quarter') {
        periodStart.setMonth(now.getMonth() - (i + 1));
        periodEnd.setMonth(now.getMonth() - i);
      } else {
        periodStart.setMonth(now.getMonth() - (i + 1));
        periodEnd.setMonth(now.getMonth() - i);
      }
      
      const periodOffers = offers.filter(o => {
        const date = new Date(o.created_at);
        return date >= periodStart && date < periodEnd;
      });
      
      const accepted = periodOffers.filter(o => o.status === 'accepted');
      const sales = accepted.reduce((sum, o) => sum + (o.total_net || 0), 0);
      
      // Target przykładowy
      const dailyTarget = 3000;
      const periodDays = dateRange === 'week' ? 1 : 
                        dateRange === 'month' ? 5 : 
                        dateRange === 'quarter' ? 30 : 30;
      
      data.push({
        period: dateRange === 'week' 
          ? periodStart.toLocaleDateString('pl-PL', { weekday: 'short' })
          : periodStart.toLocaleDateString('pl-PL', { month: 'short' }),
        sales,
        offers: periodOffers.length,
        accepted: accepted.length,
        target: dailyTarget * periodDays
      });
    }
    
    return data;
  };

  const prepareProductDistribution = (offers: any[]) => {
    const categoryMap = new Map<string, { value: number; count: number }>();
    
    offers.forEach(offer => {
      if (offer.items && Array.isArray(offer.items)) {
        offer.items.forEach((item: any) => {
          const category = item.product || 'Inne';
          const current = categoryMap.get(category) || { value: 0, count: 0 };
          
          if (offer.status === 'accepted') {
            current.value += item.totalPrice || 0;
          }
          current.count += item.quantity || 0;
          
          categoryMap.set(category, current);
        });
      }
    });
    
    const total = Array.from(categoryMap.values()).reduce((sum, c) => sum + c.value, 0);
    const colors = ['#8b5cf6', '#ec4899', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
    
    return Array.from(categoryMap.entries())
      .map(([category, data], index) => ({
        category,
        value: data.value,
        percentage: total > 0 ? (data.value / total) * 100 : 0,
        count: data.count,
        color: colors[index % colors.length]
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  };

  const prepareTopClients = (offers: any[], clients: any[]) => {
    const clientMap = new Map<string, {
      revenue: number;
      orders: number;
      lastOrder: string;
      previousRevenue: number;
    }>();
    
    const now = new Date();
    const lastMonth = new Date(now);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    
    offers.forEach(offer => {
      if (offer.client?.nip) {
        const current = clientMap.get(offer.client.nip) || {
          revenue: 0,
          orders: 0,
          lastOrder: offer.created_at,
          previousRevenue: 0
        };
        
        if (offer.status === 'accepted') {
          const offerDate = new Date(offer.created_at);
          if (offerDate >= lastMonth) {
            current.revenue += offer.total_net || 0;
          } else {
            current.previousRevenue += offer.total_net || 0;
          }
        }
        
        current.orders++;
        if (new Date(offer.created_at) > new Date(current.lastOrder)) {
          current.lastOrder = offer.created_at;
        }
        
        clientMap.set(offer.client.nip, current);
      }
    });
    
    return Array.from(clientMap.entries())
      .map(([nip, data]) => {
        const client = clients.find(c => c.nip === nip);
        const name = client?.name || offers.find(o => o.client?.nip === nip)?.client?.name || 'Nieznany';
        
        return {
          id: nip,
          name,
          revenue: data.revenue,
          orders: data.orders,
          growth: data.previousRevenue > 0 
            ? ((data.revenue - data.previousRevenue) / data.previousRevenue) * 100 
            : 100,
          avgOrderValue: data.orders > 0 ? data.revenue / data.orders : 0,
          lastOrder: data.lastOrder
        };
      })
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  };

  const preparePerformanceVsTarget = (offers: any[], dateRange: string) => {
    const periods = dateRange === 'week' ? 7 : dateRange === 'month' ? 4 : 12;
    const data = [];
    
    for (let i = 0; i < periods; i++) {
      const periodOffers = offers.filter(o => {
        // Uproszczona logika - w prawdziwej aplikacji byłoby to bardziej precyzyjne
        return true;
      });
      
      const accepted = periodOffers.filter(o => o.status === 'accepted');
      const actual = accepted.reduce((sum, o) => sum + (o.total_net || 0), 0) / periods;
      const target = 25000; // Przykładowy target tygodniowy
      
      data.push({
        period: `Okres ${i + 1}`,
        target,
        actual,
        percentage: target > 0 ? (actual / target) * 100 : 0
      });
    }
    
    return data;
  };

  const prepareRegionalAnalysis = (offers: any[], clients: any[]) => {
    const regionMap = new Map<string, {
      revenue: number;
      orders: number;
      clients: Set<string>;
    }>();
    
    offers.forEach(offer => {
      if (offer.client?.wojewodztwo || offer.delivery_region) {
        const region = offer.client?.wojewodztwo || offer.delivery_region || 'Nieznane';
        const current = regionMap.get(region) || {
          revenue: 0,
          orders: 0,
          clients: new Set()
        };
        
        if (offer.status === 'accepted') {
          current.revenue += offer.total_net || 0;
        }
        current.orders++;
        if (offer.client?.nip) {
          current.clients.add(offer.client.nip);
        }
        
        regionMap.set(region, current);
      }
    });
    
    return Array.from(regionMap.entries())
      .map(([region, data]) => ({
        region,
        revenue: data.revenue,
        orders: data.orders,
        clients: data.clients.size,
        avgOrderValue: data.orders > 0 ? data.revenue / data.orders : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
  };

  const prepareMaterialAnalysis = (offers: any[]) => {
    const materialMap = new Map<string, {
      usage: number;
      revenue: number;
      orders: number;
    }>();
    
    offers.forEach(offer => {
      if (offer.items && Array.isArray(offer.items)) {
        offer.items.forEach((item: any) => {
          const material = item.material || 'Nieznany';
          const current = materialMap.get(material) || {
            usage: 0,
            revenue: 0,
            orders: 0
          };
          
          // Oblicz powierzchnię
          const surface = item.calculations?.surface || 0;
          current.usage += surface * (item.quantity || 1);
          
          if (offer.status === 'accepted') {
            current.revenue += item.totalPrice || 0;
          }
          current.orders++;
          
          materialMap.set(material, current);
        });
      }
    });
    
    return Array.from(materialMap.entries())
      .map(([material, data]) => ({
        material,
        usage: data.usage,
        revenue: data.revenue,
        orders: data.orders,
        avgPrice: data.usage > 0 ? data.revenue / data.usage : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
  };

  const calculateTimeTrends = (offers: any[], statistics: any[]) => {
    // Analiza dni tygodnia
    const dayStats = new Map<number, number>();
    offers.forEach(offer => {
      const day = new Date(offer.created_at).getDay();
      dayStats.set(day, (dayStats.get(day) || 0) + 1);
    });
    
    const bestDayNum = Array.from(dayStats.entries())
      .sort((a, b) => b[1] - a[1])[0]?.[0] || 1;
    
    const days = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
    const bestDay = days[bestDayNum];
    
    // Analiza godzin
    const hourStats = new Map<number, number>();
    offers.forEach(offer => {
      const hour = new Date(offer.created_at).getHours();
      hourStats.set(hour, (hourStats.get(hour) || 0) + 1);
    });
    
    const bestHour = Array.from(hourStats.entries())
      .sort((a, b) => b[1] - a[1])[0]?.[0] || 10;
    
    // Średni czas odpowiedzi (od utworzenia do pierwszej akcji)
    const responseTimes: number[] = [];
    statistics.forEach(stat => {
      const offer = offers.find(o => o.number === stat.offer_number);
      if (offer) {
        const created = new Date(offer.created_at);
        const viewed = new Date(stat.viewed_at);
        const hours = (viewed.getTime() - created.getTime()) / (1000 * 60 * 60);
        responseTimes.push(hours);
      }
    });
    
    const avgResponseTime = responseTimes.length > 0
      ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
      : 24;
    
    // Średni czas zamknięcia (od utworzenia do zaakceptowania)
    const closingTimes: number[] = [];
    offers
      .filter(o => o.status === 'accepted' && o.updated_at)
      .forEach(offer => {
        const created = new Date(offer.created_at);
        const closed = new Date(offer.updated_at);
        const days = (closed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
        closingTimes.push(days);
      });
    
    const avgClosingTime = closingTimes.length > 0
      ? closingTimes.reduce((a, b) => a + b, 0) / closingTimes.length
      : 7;
    
    return {
      bestDay,
      bestHour,
      avgResponseTime,
      avgClosingTime
    };
  };

  return {
    data,
    loading,
    error,
    refresh: fetchReportData
  };
}
